import React from 'react'
import { useTokenBalance } from '../../../state/wallet/hooks'
import { useActiveWeb3React } from '../../../hooks'
import { Token, TokenAmount } from '@uniswap/sdk-core'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../state/mint/hooks'
import { Field } from '../../../state/mint/actions'
import { maxAmountSpend } from '../../../utils/maxAmountSpend'
import { calculateGasMargin, getContract } from '../../../utils'
import { parseUnits } from 'ethers/lib/utils'
import { useTransactionAdder } from '../../../state/transactions/hooks'
import { ButtonLight, ButtonPrimary } from '../../../components/Button'
import { usePrivy } from '@privy-io/react-auth'
import { RowBetween } from '../../../components/Row'
import { Text } from 'rebass'
import DurationInputNumberFork from '../DurationInputNumber'
import { roundToDecimalPlace } from '../../../utils/data'
import { XHSPX_ADDRESS } from '@hyperswap/addresses'
import XGrailTokenABI from '../../../abis/x-grail-token.json'
import CurrencyInputPanel from 'components/CurrencyInputPanel'

export const MAX_DAYS = 180
export const MIN_DAYS = 15

function daysToSeconds(days: number) {
  return days * 86400
}

function calculateVestingRatio(days: number) {
  const minDays = MIN_DAYS
  const maxDays = MAX_DAYS
  const minRatio = 0.5
  const maxRatio = 1.0

  // Clamp the days between min and max days
  if (days <= minDays) return minRatio * 100
  if (days >= maxDays) return maxRatio * 100

  // Calculate the ratio percentage using linear interpolation
  const ratio = minRatio + ((days - minDays) / (maxDays - minDays)) * (maxRatio - minRatio)

  return ratio * 100
}

interface RedeemInputProps {
  setRefresh: (value: boolean) => void
}
const RedeemInput: React.FC<RedeemInputProps> = ({ setRefresh }) => {
  const { account, chainId, library } = useActiveWeb3React()
  const { login } = usePrivy()

  const [xToken, setXToken] = React.useState<undefined | Token>(undefined)
  const userXTokenBalance = useTokenBalance(account ?? undefined, xToken ?? xToken)
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const [duration, setDuration] = React.useState<number>(15)

  const addTransaction = useTransactionAdder()

  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false)
  const { dependentField, currencies, currencyBalances, parsedAmounts, noLiquidity } = useDerivedMintInfo(
    xToken ?? undefined,
    undefined
  )

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  async function onRedeem() {
    if (!chainId || !library || !account) return //address: string, ABI: any, library: Web3Provider, account?: string
    const xtokenContract = getContract(XHSPX_ADDRESS, XGrailTokenABI.abi, library, account)

    const estimate = xtokenContract.estimateGas.redeem
    const method = xtokenContract.redeem

    const args = [parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18).toString(), daysToSeconds(duration)]

    setLoadingAdd(true)

    await estimate(...args, {})
      .then((estimatedGasLimit: any) => {
        return method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Redeem xHSPX`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh(true)
          setLoadingAdd(false)
        })
      })
      .catch((error: any) => {
        setLoadingAdd(false)
        console.error(error)
      })
  }
  const isDisabled =
    loadingAdd === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    (userXTokenBalance && parseFloat(formattedAmounts[Field.CURRENCY_A]) > parseFloat(userXTokenBalance?.toExact())) ||
    duration > MAX_DAYS ||
    duration < MIN_DAYS

  const ratio = calculateVestingRatio(duration)
  const amount = parsedAmounts[Field.CURRENCY_A]?.toExact()
  const output = amount && ratio ? (parseFloat(amount) * ratio) / 100 : '0'

  React.useEffect(() => {
    if (account && chainId) {
      setXToken(new Token(chainId, XHSPX_ADDRESS, 18, 'xHSPX', `xHSPX`))
    }
  }, [chainId, account])

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
        <CurrencyInputPanel
          value={formattedAmounts[Field.CURRENCY_A]}
          onUserInput={onFieldAInput}
          onMax={() => {
            onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
          }}
          showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
          currency={currencies[Field.CURRENCY_A]}
          id="redeem-input"
        />
        <DurationInputNumberFork setValue={setDuration} />
      </div>

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight
            onClick={() =>
              login({
                loginMethods: ['wallet'],
                walletChainType: 'ethereum-only',
                disableSignup: false,
              })
            }
          >
            Connect Wallet
          </ButtonLight>
        ) : (
          <RowBetween>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onRedeem()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingAdd ? 'Redeeming...' : 'Redeem'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
      {isDisabled ? (
        <>
          {duration > MAX_DAYS && (
            <p style={{ color: '#FD4040', marginBottom: '0', fontSize: '12px' }}>
              Redeem Duration can&apos;t exceed 180 days.
            </p>
          )}
          {duration < MIN_DAYS && (
            <p style={{ color: '#FD4040', marginBottom: '0', fontSize: '12px' }}>
              Redeem Duration can&apos;t be set to less than 15 days.
            </p>
          )}
        </>
      ) : (
        <>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '14px', paddingBottom: '6px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: 'lightgrey', fontSize: 14 }}>Redeem Ratio</span>
              <span style={{ color: '#4afc38', fontSize: 14 }}>{roundToDecimalPlace(`${ratio}`, '2')}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: 'lightgrey', fontSize: 14 }}>Output</span>
              <span style={{ color: '#4afc38', fontSize: 14 }}>{roundToDecimalPlace(`${output}`, '6')} $SWAP</span>
            </div>
          </div>
        </>
      )}
    </>
  )
}
export default RedeemInput
